/**
 * The feedback control, on every page a tester visits.
 *
 * Three rules shape it, all from the brief.
 *
 * **It never asks where you are.** Route, module, page, role, edition and
 * viewport are captured from the running application. A tester who has to
 * type "the gradebook" before saying what is wrong with it will stop telling
 * you what is wrong with it.
 *
 * **It is answerable in one tap.** The reaction alone is a complete
 * submission. Everything below it is optional and progressively revealed, so
 * the cost of leaving feedback is a single click and the ceiling is a detailed
 * account — the same control serves both without a mode switch.
 *
 * **It never blocks the page.** It is a tab at the edge, not a modal, and
 * dismissing it keeps the draft. Somebody mid-thought about a screen should
 * be able to look back at the screen.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ScreenshotAttach, type AttachedScreenshot } from './ScreenshotAttach'
import { useLocation } from 'react-router-dom'
import { api, ApiError, currentPortal } from '@/lib/api'
import { useSession } from '@/lib/session'
import {
  CATEGORY_LABELS,
  HATE_SUGGESTIONS,
  LOVE_SUGGESTIONS,
  SENTIMENTS,
  describeRoute,
  type FeedbackCategory,
  type Sentiment,
} from '@/lib/feedbackTypes'
import './feedback.css'

type Step = 'closed' | 'open' | 'bug' | 'thanks'

const CATEGORY_ORDER: FeedbackCategory[] = [
  'love', 'saves_time', 'helps_my_school', 'drives_purchase',
  'confusing', 'too_many_clicks', 'hard_to_understand', 'feels_outdated',
  'missing_something', 'have_an_idea', 'would_not_use', 'blocks_purchase',
  'found_a_bug', 'other',
]

/** Categories that read as positive, for colouring. */
const POSITIVE = new Set<FeedbackCategory>(['love', 'saves_time', 'helps_my_school', 'drives_purchase'])

export function FeedbackPanel() {
  const { session, can } = useSession()
  const location = useLocation()

  const [step, setStep] = useState<Step>('closed')
  const [sentiment, setSentiment] = useState<Sentiment | null>(null)
  const [categories, setCategories] = useState<FeedbackCategory[]>([])
  const [comment, setComment] = useState('')
  const [loves, setLoves] = useState<string[]>([])
  const [hates, setHates] = useState<string[]>([])
  const [needs, setNeeds] = useState('')
  const [wants, setWants] = useState('')
  const [showDetail, setShowDetail] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  // Bug fields, kept separate so switching modes does not lose either draft.
  const [screenshots, setScreenshots] = useState<AttachedScreenshot[]>([])
  const [unreadReplies, setUnreadReplies] = useState(0)
  const [whatHappened, setWhatHappened] = useState('')
  const [whatExpected, setWhatExpected] = useState('')
  const [canReproduce, setCanReproduce] = useState<'yes' | 'no' | 'sometimes'>('yes')
  const [severity, setSeverity] = useState<'minor' | 'annoying' | 'major' | 'blocking'>('annoying')

  const panelRef = useRef<HTMLDivElement>(null)
  const isTester = can('feedbackLab', 'create')

  const context = useMemo(() => {
    const described = describeRoute(location.pathname, currentPortal())
    return {
      route: location.pathname,
      moduleId: described.moduleId,
      moduleName: described.moduleName,
      pageName: described.pageName,
      viewport: {
        width: typeof window === 'undefined' ? 0 : window.innerWidth,
        height: typeof window === 'undefined' ? 0 : window.innerHeight,
      },
    }
  }, [location.pathname])

  const reset = useCallback(() => {
    setSentiment(null)
    setCategories([])
    setComment('')
    setLoves([])
    setHates([])
    setNeeds('')
    setWants('')
    setShowDetail(false)
    setProblem(null)
    setWhatHappened('')
    setWhatExpected('')
    setScreenshots([])
  }, [])

  useEffect(() => {
    if (!session || !isTester) return
    const controller = new AbortController()
    api
      .feedbackMe(controller.signal)
      .then((me) => setUnreadReplies(me.progress.unreadReplies ?? 0))
      .catch(() => {})
    return () => controller.abort()
    // Refetches when the panel closes after a submission, which is also when
    // a fresh reply is most likely to have arrived.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, isTester, step === 'closed'])

  // Escape closes, but the draft survives — somebody looking back at the page
  // to check something should not lose what they had written.
  useEffect(() => {
    if (step === 'closed') return
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setStep('closed')
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [step])

  if (!session || !isTester) return null

  function toggle<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value]
  }

  const canSubmit = sentiment !== null || categories.length > 0 || comment.trim().length > 0

  async function submit() {
    if (!canSubmit) return
    setSubmitting(true)
    setProblem(null)
    try {
      await api.submitFeedback({
        context,
        sentiment: sentiment ?? undefined,
        categories,
        comment: comment.trim() || undefined,
        loves,
        hates,
        needs: splitLines(needs),
        wants: splitLines(wants),
        screenshotIds: screenshots.length > 0 ? screenshots.map((shot) => shot.id) : undefined,
      })
      reset()
      setStep('thanks')
      window.setTimeout(() => setStep('closed'), 2600)
    } catch (caught) {
      setProblem(caught instanceof ApiError ? caught.message : 'We could not save that. Try again in a moment.')
    } finally {
      setSubmitting(false)
    }
  }

  async function submitBug() {
    if (whatHappened.trim().length < 5 || whatExpected.trim().length < 3) return
    setSubmitting(true)
    setProblem(null)
    try {
      await api.reportBug({
        context,
        whatHappened: whatHappened.trim(),
        whatExpected: whatExpected.trim(),
        canReproduce,
        severity,
        screenshotIds: screenshots.length > 0 ? screenshots.map((shot) => shot.id) : undefined,
      })
      reset()
      setStep('thanks')
      window.setTimeout(() => setStep('closed'), 2600)
    } catch (caught) {
      setProblem(caught instanceof ApiError ? caught.message : 'We could not save that. Try again in a moment.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {step === 'closed' && (
        <button className="fb-tab" type="button" onClick={() => setStep('open')}>
          <span className="fb-tab__dot" aria-hidden="true" />
          Give feedback
          {unreadReplies > 0 && (
            <span className="fb-tab__badge" aria-label={`${unreadReplies} unread replies`}>
              {unreadReplies}
            </span>
          )}
        </button>
      )}

      {step === 'thanks' && (
        <div className="fb-toast" role="status">
          <strong>Thanks — this is exactly the kind of feedback we need.</strong>
          <span>It goes straight to the team building Cornerstone.</span>
        </div>
      )}

      {(step === 'open' || step === 'bug') && (
        <div className="fb-panel" ref={panelRef} role="dialog" aria-label="Give feedback">
          <header className="fb-panel__head">
            <div>
              <p className="fb-panel__eyebrow">{context.moduleName}</p>
              <h2 className="fb-panel__title">{context.pageName}</h2>
            </div>
            <button className="fb-panel__close" type="button" onClick={() => setStep('closed')} aria-label="Close">
              ×
            </button>
          </header>

          <div className="fb-panel__body">
            {problem && (
              <p className="fb-panel__error" role="alert">
                {problem}
              </p>
            )}

            {step === 'open' ? (
              <>
                <section className="fb-section">
                  <h3>How does this page feel?</h3>
                  <div className="fb-reactions">
                    {SENTIMENTS.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        className={`fb-reaction ${sentiment === option.key ? 'is-selected' : ''}`}
                        onClick={() => setSentiment(sentiment === option.key ? null : option.key)}
                        aria-pressed={sentiment === option.key}
                      >
                        <span className="fb-reaction__emoji" aria-hidden="true">
                          {option.emoji}
                        </span>
                        <span className="fb-reaction__label">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </section>

                <section className="fb-section">
                  <h3>What do you think?</h3>
                  <div className="fb-chips">
                    {CATEGORY_ORDER.map((key) => (
                      <button
                        key={key}
                        type="button"
                        className={`fb-chip ${categories.includes(key) ? 'is-selected' : ''} ${
                          POSITIVE.has(key) ? 'fb-chip--positive' : ''
                        }`}
                        onClick={() => setCategories((list) => toggle(list, key))}
                        aria-pressed={categories.includes(key)}
                      >
                        {CATEGORY_LABELS[key]}
                      </button>
                    ))}
                  </div>
                </section>

                <section className="fb-section">
                  <h3>Tell us more</h3>
                  <textarea
                    className="fb-textarea"
                    rows={3}
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    placeholder="What worked? What didn't? What would you change?"
                    maxLength={4000}
                  />
                </section>

                {!showDetail ? (
                  <button className="fb-more" type="button" onClick={() => setShowDetail(true)}>
                    Add more detail — loves, hates, and what you need
                  </button>
                ) : (
                  <>
                    <section className="fb-section fb-split">
                      <div>
                        <h3>❤️ What do you love?</h3>
                        <div className="fb-chips">
                          {LOVE_SUGGESTIONS.map((label) => (
                            <button
                              key={label}
                              type="button"
                              className={`fb-chip fb-chip--positive ${loves.includes(label) ? 'is-selected' : ''}`}
                              onClick={() => setLoves((list) => toggle(list, label))}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h3>😤 What do you hate?</h3>
                        <div className="fb-chips">
                          {HATE_SUGGESTIONS.map((label) => (
                            <button
                              key={label}
                              type="button"
                              className={`fb-chip ${hates.includes(label) ? 'is-selected' : ''}`}
                              onClick={() => setHates((list) => toggle(list, label))}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </section>

                    <section className="fb-section">
                      <h3>What do you need?</h3>
                      <p className="fb-hint">Something your school could not operate without. One per line.</p>
                      <textarea
                        className="fb-textarea"
                        rows={2}
                        value={needs}
                        onChange={(event) => setNeeds(event.target.value)}
                        placeholder="State reporting export&#10;Bulk schedule changes"
                      />
                    </section>

                    <section className="fb-section">
                      <h3>What do you want?</h3>
                      <p className="fb-hint">Helpful, but you could live without it. One per line.</p>
                      <textarea
                        className="fb-textarea"
                        rows={2}
                        value={wants}
                        onChange={(event) => setWants(event.target.value)}
                        placeholder="Dark mode&#10;Saved report filters"
                      />
                    </section>
                  </>
                )}
              </>
            ) : (
              <>
                <section className="fb-section">
                  <h3>What happened?</h3>
                  <textarea
                    className="fb-textarea"
                    rows={3}
                    value={whatHappened}
                    onChange={(event) => setWhatHappened(event.target.value)}
                    placeholder="I clicked Save and the page went blank."
                  />
                </section>

                <section className="fb-section">
                  <h3>What did you expect?</h3>
                  <textarea
                    className="fb-textarea"
                    rows={2}
                    value={whatExpected}
                    onChange={(event) => setWhatExpected(event.target.value)}
                    placeholder="The grade to save and the average to update."
                  />
                </section>

                <section className="fb-section">
                  <h3>Can you make it happen again?</h3>
                  <div className="fb-chips">
                    {(['yes', 'sometimes', 'no'] as const).map((value) => (
                      <button
                        key={value}
                        type="button"
                        className={`fb-chip ${canReproduce === value ? 'is-selected' : ''}`}
                        onClick={() => setCanReproduce(value)}
                      >
                        {value === 'yes' ? 'Every time' : value === 'sometimes' ? 'Sometimes' : 'Only saw it once'}
                      </button>
                    ))}
                  </div>
                </section>

                <section className="fb-section">
                  <h3>How bad is it?</h3>
                  <div className="fb-chips">
                    {(['minor', 'annoying', 'major', 'blocking'] as const).map((value) => (
                      <button
                        key={value}
                        type="button"
                        className={`fb-chip ${severity === value ? 'is-selected' : ''}`}
                        onClick={() => setSeverity(value)}
                      >
                        {value === 'minor'
                          ? 'Minor'
                          : value === 'annoying'
                            ? 'Annoying'
                            : value === 'major'
                              ? 'Major'
                              : 'Stops me working'}
                      </button>
                    ))}
                  </div>
                </section>

                <p className="fb-hint">
                  We capture the page, your browser and the time automatically — you do not need to describe where
                  you were.
                </p>
              </>
            )}
          </div>

          <ScreenshotAttach attached={screenshots} onChange={setScreenshots} disabled={submitting} />

          <footer className="fb-panel__foot">
            {step === 'open' ? (
              <>
                <button className="fb-link" type="button" onClick={() => setStep('bug')}>
                  Report a bug instead
                </button>
                <button className="button button--primary" type="button" onClick={submit} disabled={!canSubmit || submitting}>
                  {submitting ? 'Sending…' : 'Send feedback'}
                </button>
              </>
            ) : (
              <>
                <button className="fb-link" type="button" onClick={() => setStep('open')}>
                  Back to feedback
                </button>
                <button
                  className="button button--primary"
                  type="button"
                  onClick={submitBug}
                  disabled={submitting || whatHappened.trim().length < 5 || whatExpected.trim().length < 3}
                >
                  {submitting ? 'Sending…' : 'Report bug'}
                </button>
              </>
            )}
          </footer>
        </div>
      )}
    </>
  )
}

function splitLines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 10)
}
